// Type imports from the domain model definitions
// These types define the shape of API responses and frontend domain objects
// for the new Reports system.

/**
 * API ↔ Frontend transform functions.
 *
 * The TW Reports API uses a different shape than our frontend domain types.
 * These helpers sit at the boundary (inside React Query hooks) and convert
 * API responses into the internal `Report`, `ReportModule`, `ReportTemplate`
 * shapes that every component already expects.
 */

// ── Definition ID → ModuleType mapping ──
// The API identifies modules by `moduleDefinitionId` (a number).
// Internally the frontend uses `ModuleType` (a string union) because
// module-specific components, mock generators, and min-height constraints
// are all keyed by that string.

// Module code (PascalCase) from the new API mapped to frontend ModuleType.
const MODULE_CODE_TO_MODULE_TYPE: Record<string, ModuleType> = {
  AccountOverview: 'account_overview',
  AudienceDemographics: 'audience_demographics',
  FollowerGrowth: 'follower_growth',
  FollowersCount: 'followers_count',
  TopPosts: 'top_posts',
  FacebookPageInsights: 'facebook_page_insights',
  FacebookPageStats: 'facebook_page_stats',
  FacebookTopPosts: 'facebook_top_posts',
  InstagramAccountStats: 'instagram_account_stats',
  InstagramAudience: 'instagram_audience',
  InstagramTopPosts: 'instagram_top_posts',
  TikTokAudienceCountriesDistribution: 'tiktok_audience_countries_distribution',
  TikTokAudienceGendersDistribution: 'tiktok_audience_genders_distribution',
  TikTokDailyProfileViewsAndFollowers: 'tiktok_daily_profile_views_and_followers',
  TikTokFollowersGainedSummary: 'tiktok_followers_gained_summary',
  TikTokFollowersLastDay: 'tiktok_followers_last_day',
  TikTokInteractionsTotal: 'tiktok_interactions_total',
  TikTokNetFollowersComparison: 'tiktok_net_followers_comparison',
  TikTokProfileViewsTotal: 'tiktok_profile_views_total',
  TikTokVideoViewsTotal: 'tiktok_video_views_total',
  // New module codes from updated API
  TikTokAudienceByCountry: 'tiktok_audience_countries_distribution',
  TikTokAudienceByGender: 'tiktok_audience_genders_distribution',
  TikTokAudienceGrowth: 'tiktok_daily_profile_views_and_followers',
  TikTokBestEngagingDay: 'tiktok_interactions_total',
  TikTokBestPerformingDay: 'tiktok_video_views_total',
  TikTokBestPerformingDaySummary: 'tiktok_followers_gained_summary',
  TikTokFollowers: 'tiktok_followers_last_day',
  TikTokFollowersOnline: 'tiktok_daily_profile_views_and_followers',
  TikTokFollowersOnlineSummary: 'tiktok_followers_gained_summary',
  TikTokInteractions: 'tiktok_interactions_total',
  TikTokInteractionsByDay: 'tiktok_interactions_total',
  TikTokNetFollowers: 'tiktok_net_followers_comparison',
  TikTokProfileViews: 'tiktok_profile_views_total',
  TikTokPublishingBehaviour: 'tiktok_daily_profile_views_and_followers',
  TikTokVideoEngagement: 'tiktok_video_views_total',
  TikTokVideoSources: 'tiktok_video_views_total',
  TikTokVideoViews: 'tiktok_video_views_total',
  TikTokVideoViewsByDay: 'tiktok_video_views_total',
  TikTokVideoWatchMetrics: 'tiktok_video_views_total',
};

/**
 * Lookup table mapping moduleCode to translation key pairs.
 * These keys correspond to entries in localizationv2/translations/en/reports.json
 * Format: moduleCode -> { titleKey, descriptionKey }
 */
export const MODULE_CODE_TO_TRANSLATION_KEYS: Record<string, { titleKey: string; descriptionKey: string }> = {
  TikTokProfileViews: {
    titleKey: 'tiktok_profile_views_title',
    descriptionKey: 'tiktok_profile_views_description',
  },
  TikTokFollowers: {
    titleKey: 'tiktok_followers_title',
    descriptionKey: 'tiktok_followers_description',
  },
  TikTokVideoViews: {
    titleKey: 'tiktok_video_views_title',
    descriptionKey: 'tiktok_video_views_description',
  },
  TikTokInteractions: {
    titleKey: 'tiktok_interactions_title',
    descriptionKey: 'tiktok_interactions_description',
  },
  TikTokNetFollowers: {
    titleKey: 'tiktok_net_followers_title',
    descriptionKey: 'tiktok_net_followers_description',
  },
  TikTokAudienceGrowth: {
    titleKey: 'tiktok_audience_growth_title',
    descriptionKey: 'tiktok_audience_growth_description',
  },
  TikTokPublishingBehaviour: {
    titleKey: 'tiktok_publishing_behaviour_title',
    descriptionKey: 'tiktok_publishing_behaviour_description',
  },
  TikTokFollowersOnline: {
    titleKey: 'tiktok_followers_online_title',
    descriptionKey: 'tiktok_followers_online_description',
  },
  TikTokBestPerformingDaySummary: {
    titleKey: 'tiktok_best_performing_day_summary_title',
    descriptionKey: 'tiktok_best_performing_day_summary_description',
  },
  TikTokFollowersOnlineSummary: {
    titleKey: 'tiktok_followers_online_summary_title',
    descriptionKey: 'tiktok_followers_online_summary_description',
  },
  TikTokVideoViewsByDay: {
    titleKey: 'tiktok_video_views_by_day_title',
    descriptionKey: 'tiktok_video_views_by_day_description',
  },
  TikTokInteractionsByDay: {
    titleKey: 'tiktok_interactions_by_day_title',
    descriptionKey: 'tiktok_interactions_by_day_description',
  },
  TikTokBestPerformingDay: {
    titleKey: 'tiktok_best_performing_day_title',
    descriptionKey: 'tiktok_best_performing_day_description',
  },
  TikTokBestEngagingDay: {
    titleKey: 'tiktok_best_engaging_day_title',
    descriptionKey: 'tiktok_best_engaging_day_description',
  },
  TikTokVideoSources: {
    titleKey: 'tiktok_video_sources_title',
    descriptionKey: 'tiktok_video_sources_description',
  },
  TikTokVideoEngagement: {
    titleKey: 'tiktok_video_engagement_title',
    descriptionKey: 'tiktok_video_engagement_description',
  },
  TikTokVideoWatchMetrics: {
    titleKey: 'tiktok_video_watch_metrics_title',
    descriptionKey: 'tiktok_video_watch_metrics_description',
  },
  TikTokAudienceByGender: {
    titleKey: 'tiktok_audience_by_gender_title',
    descriptionKey: 'tiktok_audience_by_gender_description',
  },
  TikTokAudienceByCountry: {
    titleKey: 'tiktok_audience_by_country_title',
    descriptionKey: 'tiktok_audience_by_country_description',
  },
};

/**
 * Get translation keys for a module code.
 * Returns the titleKey and descriptionKey that correspond to entries
 * in localizationv2/translations/en/sendible360.json
 */
export const getModuleTranslationKeys = (moduleCode: string): { titleKey: string; descriptionKey: string } | null => {
  return MODULE_CODE_TO_TRANSLATION_KEYS[moduleCode] ?? null;
};

/**
 * Convert a space-separated name to PascalCase.
 * E.g., "Top Posts" → "TopPosts", "TikTok Daily Views" → "TikTokDailyViews"
 */
const nameToPascalCase = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Resolve a moduleDefinitionId to a ModuleType using a loaded definitions list.
 * Prefers moduleCode (new API field) over name-based lookup.
 * Falls back to 'account_overview' if the definition is unknown.
 */
export const resolveModuleType = (moduleDefinitionId: number, definitions: ModuleDefinition[] | undefined): ModuleType => {
  if (!definitions) return 'account_overview';

  const def = definitions.find((d) => d.id === moduleDefinitionId);

  if (!def) return 'account_overview';

  // Prefer moduleCode-based resolution (new API)
  if (def.moduleCode) {
    const resolved = MODULE_CODE_TO_MODULE_TYPE[def.moduleCode];

    if (resolved) return resolved;
  }

  // Fallback to name-based resolution
  const pascalCaseName = nameToPascalCase(def.name);
  const resolved = MODULE_CODE_TO_MODULE_TYPE[pascalCaseName];

  if (resolved) return resolved;

  return 'account_overview';
};

// ── API module → frontend ReportModule ──

export const transformApiModule = (apiModule: ApiReportModule, definitions?: ModuleDefinition[]): ReportModule => ({
  reportModuleId: String(apiModule.id),
  moduleDefinitionId: apiModule.moduleDefinitionId,
  moduleType: resolveModuleType(apiModule.moduleDefinitionId, definitions),
  customName: apiModule.displayName || null,
  position: {
    x: apiModule.layout.position.x,
    y: apiModule.layout.position.y,
    w: apiModule.layout.size.width,
    h: apiModule.layout.size.height,
  },
  config: {},
  chartType: apiModule.chartType,
});

// ── API template → frontend ReportTemplate ──

// Platform type map used to infer the template's platformType from its modules.
const DEFINITION_CATEGORY_TO_PLATFORM: Record<string, PlatformType> = {
  Facebook: 'Facebook',
  TikTok: 'TikTok',
  Instagram: 'Instagram',
  Common: 'Facebook', // default fallback
};

export const transformApiTemplate = (apiTemplate: ApiReportTemplate, definitions?: ModuleDefinition[]): ReportTemplate => {
  // Infer platform from the first module's definition category
  let platformType: PlatformType = 'Facebook';

  if (definitions && apiTemplate.templateModules.length > 0) {
    const firstDefId = apiTemplate.templateModules[0].moduleDefinitionId;
    const firstDef = definitions.find((d) => d.id === firstDefId);

    if (firstDef) {
      platformType = DEFINITION_CATEGORY_TO_PLATFORM[firstDef.category] ?? 'Facebook';
    }
  }

  // Convert templateModules to frontend ReportModule shape
  const modules: ReportModule[] = apiTemplate.templateModules
    .filter((tm) => tm?.layout?.position && tm?.layout?.size)
    .map((tm, idx) => {
      const moduleType = resolveModuleType(tm.moduleDefinitionId, definitions);

      return {
        reportModuleId: `tmpl_${apiTemplate.id}_mod_${idx}`,
        moduleDefinitionId: tm.moduleDefinitionId,
        moduleType,
        customName: null,
        position: {
          x: tm.layout.position.x,
          y: tm.layout.position.y,
          w: tm.layout.size.width,
          h: tm.layout.size.height,
        },
        config: {},
      };
    });

  return {
    id: String(apiTemplate.id),
    name: apiTemplate.name,
    description: apiTemplate.description,
    platformType,
    moduleCount: apiTemplate.templateModules.length,
    modules,
    layout: { columns: 4, order: modules.map((m) => m.reportModuleId) },
  };
};

// ── API report list → frontend ReportListItem ──

export const transformApiReportToListItem = (apiReport: ApiReport): ReportListItem => ({
  id: String(apiReport.id),
  name: apiReport.name,
  moduleCount: apiReport.modules?.length ?? 0,
  profileIds: apiReport.profileIds,
  updatedAt: apiReport.updatedAt ?? apiReport.updated_at ?? apiReport.createdAt ?? apiReport.created_at,
});

// ── Normalise API platform types to frontend ──
// The API may return 'FacebookPage' which normalises to 'Facebook'.

const API_PLATFORM_NORMALISE: Record<string, PlatformType> = {
  FacebookPage: 'Facebook',
  'facebook(page)': 'Facebook',
  facebookpage: 'Facebook',
  facebook: 'Facebook',
  tiktokbusiness: 'TikTok',
  tiktok: 'TikTok',
  InstagramBusiness: 'Instagram',
  instagrambusiness: 'Instagram',
  instagrampost: 'Instagram',
  instagram: 'Instagram',
  X: 'X',
  x: 'X',
  LinkedInCompanyPage: 'LinkedIn',
  LinkedInProfile: 'LinkedIn',
  linkedincompanypage: 'LinkedIn',
  linkedinprofile: 'LinkedIn',
  linkedin: 'LinkedIn',
};

export const normalisePlatform = (apiPlatform: string): PlatformType => {
  if (API_PLATFORM_NORMALISE[apiPlatform]) return API_PLATFORM_NORMALISE[apiPlatform];

  // Fallback: try lowercase lookup for case-insensitive match
  const lower = apiPlatform.toLowerCase();

  if (API_PLATFORM_NORMALISE[lower]) return API_PLATFORM_NORMALISE[lower];

  return apiPlatform as PlatformType;
};
